import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiJson } from '../api/client.js';
import { FormIcon } from '../forms/FormIcon.jsx';
import { FormRenderer, buildSubmissionBody } from '../forms/FormRenderer.jsx';
import { formatDateTime, formatDay } from '../forms/formSchema.js';
import '../forms/forms.css';

export function MyFormFillPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [another, setAnother] = useState(false);
  const draftRef = useRef(null);

  const load = () =>
    apiJson(`/api/my/forms/${id}`)
      .then((next) => {
        setData(next);
        draftRef.current = next.draft || null;
      })
      .catch((e) => setError(e.message));

  useEffect(() => {
    setAnother(false);
    void load();
  }, [id]);

  const autosave = useCallback(
    async ({ values, files, keepFileIds }) => {
      const result = await apiJson(`/api/my/forms/${id}/draft`, {
        method: 'POST',
        body: buildSubmissionBody(values, files, {
          draft_id: draftRef.current?.id || '',
          keep_files: JSON.stringify(keepFileIds || []),
        }),
      });
      if (result.ignored) return { ignored: true };
      draftRef.current = result.draft;
      return result.draft;
    },
    [id],
  );

  const submit = async (values, files, _honeypot, keepFileIds) => {
    const result = await apiJson(`/api/my/forms/${id}/submit`, {
      method: 'POST',
      body: buildSubmissionBody(values, files, {
        draft_id: draftRef.current?.id || '',
        keep_files: JSON.stringify(keepFileIds || []),
      }),
    });
    setTimeout(() => {
      setAnother(false);
      void load();
    }, 2200);
    return result;
  };

  if (error) {
    return (
      <div className="fm-portal">
        <Link to="/my/forms" className="fm-back">
          <FormIcon name="back" size={16} /> Back to forms
        </Link>
        <p className="fm-alert fm-alert--error">{error}</p>
      </div>
    );
  }
  if (!data) return <p className="fm-empty">Loading…</p>;

  const { form, submission, draft, can_submit: canSubmit } = data;
  const resumeDraft = another ? null : draft;
  const showAnswers = submission && !resumeDraft && !another;
  const filesFor = (key) => (submission?.files || []).filter((f) => f.field_key === key).map((f) => f.name);

  return (
    <div className="fm-portal fm-portal-fill">
      <Link to="/my/forms" className="fm-back">
        <FormIcon name="back" size={16} /> Back to forms
      </Link>

      {showAnswers ? (
        <div className="fm-card fm-portal-done">
          <div className="fm-portal-done-head">
            <span className="fm-success-icon">
              <FormIcon name="check" size={22} strokeWidth={2.4} />
            </span>
            <div>
              <h2>{form.title}</h2>
              <p>
                Submitted {formatDateTime(submission.created_at)}
                {submission.is_late && form.due_date ? ` · after the due date (${formatDay(form.due_date)})` : ''}
              </p>
            </div>
            {canSubmit ? (
              <button
                type="button"
                className="fm-btn fm-btn--blue"
                onClick={() => {
                  draftRef.current = null;
                  setAnother(true);
                }}
              >
                Submit another response
              </button>
            ) : null}
          </div>
          <dl className="fm-answers">
            {form.fields
              .filter((f) => !['divider', 'paragraph'].includes(f.type))
              .map((field) =>
                field.type === 'heading' ? (
                  <h4 key={field.id} className="fm-answers-heading">
                    {field.label}
                  </h4>
                ) : (
                  <div key={field.id} className="fm-answer">
                    <dt>{field.label}</dt>
                    <dd>
                      {field.type === 'file'
                        ? filesFor(field.key).join(', ') || <span className="fm-empty-answer">No file</span>
                        : (() => {
                            const v = submission.payload?.[field.key];
                            if (v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length)) return <span className="fm-empty-answer">No answer</span>;
                            return Array.isArray(v) ? v.join(', ') : String(v);
                          })()}
                    </dd>
                  </div>
                ),
              )}
          </dl>
          <p className="fm-hint">Need to change something? Reply to your project team and they will update it for you.</p>
        </div>
      ) : (
        <div className="fm-card fm-public-card">
          <FormRenderer
            key={resumeDraft?.id ? `draft-${resumeDraft.id}` : another ? 'another' : 'new'}
            definition={form}
            onSubmit={canSubmit ? submit : undefined}
            onAutosave={canSubmit ? autosave : undefined}
            initialValues={resumeDraft?.payload || {}}
            initialFiles={resumeDraft?.files || []}
            initialSavedAt={resumeDraft?.updated_at || null}
            disabled={!canSubmit}
            notice={
              <>
                {form.due_date ? (
                  <p className={`fm-due-note${new Date(`${form.due_date}T23:59:59`) < new Date() ? ' is-late' : ''}`}>
                    <FormIcon name="date" size={15} /> Due {formatDay(form.due_date)}
                  </p>
                ) : null}
                {!canSubmit ? <p className="fm-alert fm-alert--warn">This form is no longer accepting responses.</p> : null}
              </>
            }
          />
        </div>
      )}
    </div>
  );
}
